import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import withStyles from '@mui/styles/withStyles';
import Alert from 'shared/atoms/Alert';
import FullWidthTabs from 'shared/atoms/FullWidthTabs';
import SearchField from 'shared/atoms/SearchField';
import T from 'shared/atoms/Typography';
import Dialog from 'shared/molecules/Dialog';
import { newSelectTranslation } from 'shared/utils';
import theme from 'shared/themes/default';
import { usePlaylist } from 'shared/sections/PlaylistAdministration/context';

import CourseAndPreviewPanel from './CourseAndPreviewPanel';
import { listCourses } from '../graphql/queries';

const SideSpaceWrapper = styled.div`
  padding: 0 1rem;
`;

const SubtitleWrapper = styled.div`
  padding: 0 1rem 1rem 1rem;
`;

const styles = {
  dialogPaper: {
    maxHeight: '88vh',
    margin: '0',
  },
};

function SelectCoursesAndPreview({
  classes,
  formStatus,
  handleSubmit,
  onClose,
  playlist,
}) {
  const { locale, organization, useLazyGraphQLQuery } = usePlaylist();

  const [coursesToSelect, setCoursesToSelect] = useState([]);
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [coursesSelected, setCoursesSelected] = useState(
    playlist?.courses || []
  );
  const [initPage, setInitPage] = useState(1);
  const [submitting, setSubmitting] = useState();

  const [listCoursesQuery, listCoursesResponse] =
    useLazyGraphQLQuery(listCourses);
  const limit = 500;

  const { t } = useTranslation();

  const onSubmit = (value) => {
    setSubmitting(true);
    handleSubmit(value);
  };

  const toggleCourseHandler = (id, page) => {
    setInitPage(page);
    const courseAlreadySelected = coursesSelected.find(
      ({ id: courseId }) => courseId === id
    );
    if (courseAlreadySelected) {
      setCoursesSelected(coursesSelected.filter((course) => course.id !== id));
    } else {
      const course = coursesToSelect.find(
        ({ id: courseId }) => courseId === id
      );
      const newCourse = { ...course };
      setCoursesSelected([...coursesSelected, newCourse]);
    }
  };

  useEffect(() => {
    setSubmitting(false);
  }, [formStatus]);

  useEffect(() => {
    listCoursesQuery({
      variables: {
        pagination: {
          offset: 0,
          limit,
        },
        filter: {
          status: 'PUBLISHED',
        },
        organizationId: organization?.id,
      },
    });
  }, []);

  useEffect(() => {
    const { data, loading } = listCoursesResponse;

    if (!loading && data) {
      if (
        data.listCourses.length > 0 &&
        data.listCourses.length % limit === 0
      ) {
        listCoursesResponse.fetchMore({
          variables: {
            pagination: {
              limit,
              offset: data.listCourses.length,
            },
          },
        });
      } else {
        setCoursesToSelect(data.listCourses);
      }
    }
  }, [listCoursesResponse]);

  const tab1Handler = (
    <CourseAndPreviewPanel
      coursesSelected={coursesSelected}
      coursesToSelect={coursesToSelect}
      initPage={initPage}
      onCourseClickHandler={toggleCourseHandler}
      loading={listCoursesResponse.loading || !coursesToSelect?.length}
      searchTerm={searchTerm}
    />
  );

  const tab2Handler = (
    <CourseAndPreviewPanel
      coursesSelected={coursesSelected}
      coursesToSelect={coursesSelected}
      initPage={initPage}
      onCourseClickHandler={toggleCourseHandler}
      loading={listCoursesResponse.loading || !coursesToSelect?.length}
      searchTerm={searchTerm}
    />
  );

  const onChangeTabHandler = () => {
    setInitPage(1);
    setSearchTerm('');
    setSelectedTabIndex(selectedTabIndex === 0 ? 1 : 0);
  };

  const tabsToSelect = [
    { label: t('playlist.pickCourses'), component: tab1Handler },
    {
      label: `${t('playlist.previewPlaylists')} (${coursesSelected.length})`,
      component: tab2Handler,
    },
  ];

  const actions = [
    {
      disabled: !coursesSelected.length,
      handler: () => onSubmit(coursesSelected),
      label: t('generic.save'),
    },
  ];

  return (
    <Dialog
      open
      classes={{ paper: classes.dialogPaper }}
      onClose={onClose}
      ariaLabel={t('playlist.selectPlaylistCourses')}
      title={t('playlist.selectPlaylistCourses')}
      loading={submitting}
      actions={actions}
      contentPadding="0"
      formStatus={formStatus}
    >
      <SubtitleWrapper>
        <T
          color={theme.palette.darkDistinct}
          variant="paragraph"
        >{`"${newSelectTranslation(playlist.name, locale)}"`}</T>
      </SubtitleWrapper>
      {formStatus && (
        <SideSpaceWrapper>
          <Alert
            message={formStatus.message}
            severity={formStatus.success ? 'success' : 'error'}
            theme="light"
          />
        </SideSpaceWrapper>
      )}
      {!formStatus && (
        <FullWidthTabs
          tabsToSelect={tabsToSelect}
          onChangeTabHandler={onChangeTabHandler}
          selectedTabIndex={selectedTabIndex}
        >
          <SearchField
            theme="light"
            ariaLabel={t('playlist.searchCourses')}
            placeholder={t('playlist.searchCourses')}
            value={searchTerm}
            onClear={() => setSearchTerm('')}
            onChange={({ value }) => setSearchTerm(value)}
          />
        </FullWidthTabs>
      )}
    </Dialog>
  );
}

SelectCoursesAndPreview.propTypes = {
  classes: PropTypes.object.isRequired,
  formStatus: PropTypes.object,
  handleSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  playlist: PropTypes.object.isRequired,
};

export default withStyles(styles)(SelectCoursesAndPreview);
